import { clsx, type ClassValue } from 'clsx';
import { extendTailwindMerge } from 'tailwind-merge';

/**
 * The project's own font-size names (tailwind.config.ts → theme.extend.fontSize).
 * tailwind-merge does not read the Tailwind config, so without this it took
 * `text-body-sm` for a text COLOUR, saw it "conflict" with `text-on-surface`,
 * and silently dropped whichever came first — every cn() that combined a
 * custom size with a colour lost its size. Keep in sync with the config.
 */
const FONT_SIZE_KEYS = [
  'label-caps', 'label-sm', 'data-lg', 'data-md', 'headline-md', 'headline-lg',
  'display-sm', 'body-xs', 'body-sm', 'body-md', 'body-lg',
];

const twMerge = extendTailwindMerge({
  extend: { classGroups: { 'font-size': [{ text: FONT_SIZE_KEYS }] } },
});

/** Merge conditional class names and de-dupe conflicting Tailwind classes. */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

/** Initials for an avatar fallback, e.g. "Shoaib Khan" -> "SK". */
export function initials(name?: string | null): string {
  if (!name) return '';
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
}

const DEPT_ACRONYMS = new Set(['hr', 'it', 'admin', 'ceo', 'cto', 'cfo', 'bde', 'bte', 'pe', 'oe', 'qa']);

/** A stored department/role enum as a person would write it: "digital_marketing" → "Digital Marketing", "hr" → "HR". */
export function humanizeEnum(value?: string | null): string {
  const raw = String(value ?? '').trim();
  if (!raw) return '—';
  return raw.replaceAll('_', ' ').replaceAll('-', ' ').split(/\s+/).map((word) => {
    const lower = word.toLowerCase();
    return DEPT_ACRONYMS.has(lower) ? lower.toUpperCase() : lower.charAt(0).toUpperCase() + lower.slice(1);
  }).join(' ');
}

/** Compact relative timestamp, e.g. "just now", "5m ago", "3h ago", "2d ago". */
export function timeAgo(input?: string | number | Date): string {  if (!input) return '';
  const d = new Date(input);
  const ts = d.getTime();
  if (Number.isNaN(ts)) return '';
  const diff = Date.now() - ts;
  const sec = Math.round(diff / 1000);
  if (sec < 45) return 'just now';
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day < 7) return `${day}d ago`;
  return d.toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' });
}

/** Today's date as YYYY-MM-DD in the user's own timezone. toISOString() is UTC,
 *  which in India gives yesterday's date until 5:30 AM. */
export function localToday(date: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}
